import React from 'react';
import { ClassSession } from '../utils/scheduleUtils';
import { useLanguage } from '../hooks/useLanguage';
import { useTranslation } from '../translations';
import { FaFileAlt, FaBook, FaCommentAlt, FaBan } from 'react-icons/fa';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { formatTimeWithTimezones } from '../utils/dateUtils';
import { isPaymentUrgent, getPaymentStatusHint } from '../utils/paymentUtils';
import { getHolidayOnDate } from '../utils/holidayUtils';
import { User } from '../types/interfaces';
export interface ScheduleCalendarDayProps<T extends ClassSession> {
  date: Date;
  isToday: boolean;
  classes: T[];
  paymentsDue: { user: User; classSession: ClassSession }[] | boolean;
  onClassCountClick?: (e: React.MouseEvent, date: Date) => void;
  onPaymentPillClick?: (e: React.MouseEvent, date: Date, classes: T[]) => void;
  onDayClick?: (date: Date, classes: T[]) => void;
  materialsInfo?: Map<string, { hasSlides: boolean; hasLinks: boolean }>;
  homeworkInfo?: Map<string, number>; // Map of classId to homework count for this date
  onHomeworkPillClick?: (e: React.MouseEvent, date: Date) => void;
  homeworkFeedbackInfo?: Map<string, boolean>; // Map of classId_date to boolean indicating if feedback exists
  paymentStatus?: { isCompleted: boolean; completedAt?: string };
  /** Students/users with a birthday on this calendar day (same month as API payload). */
  scheduleBirthdays?: { name: string }[];
  onBirthdayPillClick?: (e: React.MouseEvent, date: Date) => void;
  /** When true, birthday pill shows N Birthday(s); when false, a single friendly string (students). */
  isScheduleAdmin: boolean;
}

export function ScheduleCalendarDay<T extends ClassSession>({
  date,
  isToday,
  classes,
  paymentsDue,
  onClassCountClick,
  onPaymentPillClick,
  onDayClick,
  materialsInfo,
  homeworkInfo,
  onHomeworkPillClick,
  homeworkFeedbackInfo,
  paymentStatus,
  scheduleBirthdays = [],
  onBirthdayPillClick,
  isScheduleAdmin
}: ScheduleCalendarDayProps<T>) {
  const { language } = useLanguage();
  const t = useTranslation(language);
  
  // Determine if it's a payment day and if payment is soon
  const isPaymentDay = Array.isArray(paymentsDue) ? paymentsDue.length > 0 : !!paymentsDue;
  const isPaymentSoon = isPaymentDay && !paymentStatus?.isCompleted && isPaymentUrgent(date);

  // Check if any class on this day has materials
  const hasMaterials = React.useMemo(() => {
    if (!materialsInfo || classes.length === 0) return false;
    
    const dateStr = date.toISOString().split('T')[0];
    return classes.some(classItem => {
      const key = `${classItem.id}_${dateStr}`;
      return materialsInfo.has(key);
    });
  }, [classes, date, materialsInfo]);

  // Check if any class on this day has homework
  const hasHomework = React.useMemo(() => {
    if (!homeworkInfo || classes.length === 0) return false;
    
    const dateStr = date.toISOString().split('T')[0];
    
    const result = classes.some(classItem => {
      const key = `${classItem.id}_${dateStr}`;
      const hasHw = homeworkInfo.has(key) && homeworkInfo.get(key)! > 0;
      return hasHw;
    });
    
    return result;
  }, [classes, date, homeworkInfo]);

  // Check if any homework on this day has feedback
  const hasHomeworkFeedback = React.useMemo(() => {
    if (!homeworkFeedbackInfo || classes.length === 0) return false;
    
    const dateStr = date.toISOString().split('T')[0];
    
    return classes.some(classItem => {
      const key = `${classItem.id}_${dateStr}`;
      return homeworkFeedbackInfo.has(key) && homeworkFeedbackInfo.get(key) === true;
    });
  }, [classes, date, homeworkFeedbackInfo]);

  // Check for exception classes
  const hasCancelledClasses = React.useMemo(() => {
    return classes.some(classItem => classItem.isCancelled);
  }, [classes]);

  const hasRescheduledClasses = React.useMemo(() => {
    return classes.some(classItem => classItem.isRescheduledTo);
  }, [classes]);

  const hasBirthdays = scheduleBirthdays.length > 0;
  const holiday = getHolidayOnDate(date);
  const holidayName = holiday ? t[holiday.nameKey] : null;


  // Filter out cancelled classes for display counts (but keep them for visual indication)
  const activeClasses = React.useMemo(() => {
    return classes.filter(c => !c.isCancelled);
  }, [classes]);

  const cancelledClasses = React.useMemo(() => {
    return classes.filter(c => c.isCancelled);
  }, [classes]);

  // Get total homework count for this day
  const getTotalHomeworkCount = (): number => {
    if (!homeworkInfo || classes.length === 0) return 0;
    
    const dateStr = date.toISOString().split('T')[0];
    let count = 0;
    
    classes.forEach(classItem => {
      const key = `${classItem.id}_${dateStr}`;
      if (homeworkInfo.has(key)) {
        count += homeworkInfo.get(key)!;
      }
    });
    
    return count;
  };

  // Handle day click
  const handleDayClick = () => {
    if (onDayClick) {
      onDayClick(date, classes);
    }
  };

  // Handle class count click
  const handleClassCountClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the calendar day click
    if (onClassCountClick) {
      onClassCountClick(e, date);
    }
  };

  // Handle payment pill click
  const handlePaymentPillClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the calendar day click
    if (onPaymentPillClick) {
      onPaymentPillClick(e, date, classes);
    }
  };

  // Handle homework pill click
  const handleHomeworkPillClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the calendar day click
    if (onHomeworkPillClick) {
      onHomeworkPillClick(e, date);
    }
  };
  
  // Function to get day name from dayOfWeek number
  const getDayName = (dayOfWeek: number | undefined): string => {
    if (dayOfWeek === undefined) return '';
    
    const days = [
      t.sunday || 'Sunday',
      t.monday || 'Monday', 
      t.tuesday || 'Tuesday', 
      t.wednesday || 'Wednesday', 
      t.thursday || 'Thursday', 
      t.friday || 'Friday', 
      t.saturday || 'Saturday'
    ];
    
    return days[dayOfWeek];
  };
  
  const createBirthdayTooltip = (): string => {
    if (!hasBirthdays) return '';
    return scheduleBirthdays.map((b) => b.name).join('\n\n');
  };

  // Create tooltip text for payment pill
  const createPaymentTooltip = (): string => {
    if (!Array.isArray(paymentsDue) || !paymentsDue.length) return '';
    
    return paymentsDue.map(({ user, classSession }) => {
      const dayName = getDayName(classSession.dayOfWeek);
      const time = formatSingleClassTime(classSession);
      
      // Show timezone if available and different from user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const tzInfo = classSession.timezone && classSession.timezone !== userTimezone 
        ? ` (${classSession.timezone.replace(/^.*\//, '')})` 
        : '';
      
      const amountText = classSession.paymentConfig?.amount && classSession.paymentConfig?.currency
        ? ` (${classSession.paymentConfig.currency} ${classSession.paymentConfig.amount.toFixed(2)})`
        : '';
      
      return `${user.name}: ${dayName} ${time}${tzInfo}${amountText}`;
    }).join('\n');
  };

  // Format time for a single class
  const formatSingleClassTime = (classItem: ClassSession): string => {
    // Get the start and end times, considering multiple schedules
    let startTime = classItem.startTime;
    let endTime = classItem.endTime;
    let timezone = classItem.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Find the matching schedule for the day of week if we have multiple schedules
    if (classItem.scheduleType === 'multiple' && Array.isArray(classItem.schedules)) {
      // Use the calendar day's date to determine day of week
      const dayOfWeek = date.getDay();
      
      // First try to find schedule matching this specific calendar day
      const matchingSchedule = classItem.schedules.find(schedule => 
        schedule.dayOfWeek === dayOfWeek
      );
      
      // If found a matching schedule for this day, use it
      if (matchingSchedule) {
        startTime = matchingSchedule.startTime;
        endTime = matchingSchedule.endTime;
        // Use schedule timezone if available
        if (matchingSchedule.timezone) {
          timezone = matchingSchedule.timezone;
        }
      }
      // If no matching schedule for this day but we have a specific dayOfWeek on the class
      else if (classItem.dayOfWeek !== undefined) {
        const fallbackSchedule = classItem.schedules.find(schedule => 
          schedule.dayOfWeek === classItem.dayOfWeek
        );
        
        if (fallbackSchedule) {
          startTime = fallbackSchedule.startTime;
          endTime = fallbackSchedule.endTime;
          // Use schedule timezone if available
          if (fallbackSchedule.timezone) {
            timezone = fallbackSchedule.timezone;
          }
        }
      }
    }
    
    if (!startTime || !endTime) return '';
    
    // Get the user's local timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Use our utility function to format the time with both timezones
    return formatTimeWithTimezones(startTime, endTime, timezone, userTimezone, date);
  };

  return (
    <div className="h-full flex flex-col" onClick={handleDayClick}>
      {/* Indicators */}
      <div className="calendar-day-indicators">
        {activeClasses.length > 0 && (
          <div key="class-indicator" className="indicator class-indicator" title="Has classes" />
        )}
        {hasCancelledClasses && (
          <div key="cancelled-indicator" className="indicator cancelled-indicator" title={t.exceptions?.cancelledClass || 'Cancelled class'} />
        )}
        {hasRescheduledClasses && (
          <div key="rescheduled-indicator" className="indicator rescheduled-indicator" title="Rescheduled class" />
        )}
        {isPaymentDay && (
          <div
            key="payment-indicator"
            className={`indicator ${
              paymentStatus?.isCompleted
                ? 'payment-completed-indicator'
                : isPaymentSoon
                ? 'payment-soon-indicator'
                : 'payment-indicator'
            }`}
            title={
              paymentStatus?.isCompleted
                ? t.paymentCompleted
                : getPaymentStatusHint(date, t)
            }
          />
        )}
        {hasMaterials && (
          <div key="materials-indicator" className="indicator material-indicator" title="Has materials" />
        )}
        {hasHomework && (
          <div key="homework-indicator" className="indicator homework-indicator" title="Has homework" />
        )}
        {hasHomeworkFeedback && (
          <div key="feedback-indicator" className="indicator feedback-indicator" title="Has feedback from teacher" />
        )}
        {hasBirthdays && (
          <div
            key="birthday-indicator"
            className="indicator birthday-indicator"
            title={
              isScheduleAdmin
                ? `${scheduleBirthdays.length} ${scheduleBirthdays.length === 1 ? t.birthday : t.birthdays}`
                : createBirthdayTooltip() || t.happyBirthdayCalendarPill
            }
          >
            🎂
          </div>
        )}
        {holidayName && (
          <div
            key="holiday-indicator"
            className="indicator holiday-indicator"
            title={holidayName}
          />
        )}
      </div>

      {/* Date */}
      <div className="flex flex-col items-center">
        <div className={`date-number ${isToday ? 'text-[#6366f1]' : ''} ${
          isPaymentDay 
            ? paymentStatus?.isCompleted
              ? 'text-[#22c55e]'  // Green text for completed payments
              : isPaymentSoon
              ? 'text-[#ef4444]'  // Red text for soon payments
              : 'text-[#f59e0b]'  // Yellow text for normal payments
            : ''
        }`}>
          {date.getDate()}
        </div>
      </div>

      {/* Class count and payment pills */}
      <div className="class-details">
        <div className="flex flex-col items-center gap-2">
          {/* Active classes pill */}
          {activeClasses.length > 0 && (
            <div
              key="class-count-pill"
              className={`calendar-pill class-count-pill ${hasRescheduledClasses ? 'rescheduled' : ''}`}
              onClick={handleClassCountClick}
            >
              {activeClasses.length === 1 
                ? (() => {
                    // Get the start and end times, considering multiple schedules
                    let classItem = activeClasses[0];
                    let startTime = classItem.startTime;
                    let endTime = classItem.endTime;
                    let timezone = classItem.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                    
                    // If it's a multiple schedule class, find the right schedule for this day
                    if (classItem.scheduleType === 'multiple' && Array.isArray(classItem.schedules)) {
                      const dayOfWeek = date.getDay();
                      const matchingSchedule = classItem.schedules.find(s => s.dayOfWeek === dayOfWeek);
                      
                      if (matchingSchedule) {
                        startTime = matchingSchedule.startTime;
                        endTime = matchingSchedule.endTime;
                        timezone = matchingSchedule.timezone || timezone;
                      }
                    }

                    // Get the user's local timezone
                    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    
                    // Use our utility function to format the time with both timezones
                    // Don't show source time in the pill to save space
                    return formatTimeWithTimezones(startTime, endTime, timezone, userTimezone, date, false);
                  })()
                : `${activeClasses.length} ${t.class}`
              }
              {hasMaterials && (
                <span className="material-icon text-white">
                  <FaFileAlt className="inline-block w-3 h-3" />
                </span>
              )}
              {hasRescheduledClasses && (
                <span className="ml-1" title={t.exceptions?.rescheduledFrom || 'Rescheduled'}>
                  <ArrowPathIcon className="inline-block w-3 h-3" />
                </span>
              )}
            </div>
          )}

          {/* Cancelled classes pill */}
          {cancelledClasses.length > 0 && (
            <div
              key="cancelled-class-pill"
              className="calendar-pill cancelled-class-pill"
              onClick={handleClassCountClick}
              title={t.exceptions?.cancelledClass || 'Cancelled'}
            >
              <span className="line-through opacity-70">
                {cancelledClasses.length === 1 ? formatSingleClassTime(cancelledClasses[0]) : `${cancelledClasses.length} ${t.class}`}
              </span>
              <FaBan className="inline-block w-3 h-3 ml-1 text-red-400" />
            </div>
          )}

          {isPaymentDay && (
            <div 
              key="payment-pill"
              className={`calendar-pill payment-pill ${
                paymentStatus?.isCompleted
                  ? 'completed'
                  : isPaymentSoon
                  ? 'soon'
                  : 'normal'
              }`}
              onClick={handlePaymentPillClick}
              title={createPaymentTooltip()}
            >
              {paymentStatus?.isCompleted ? t.paymentCompleted : t.paymentDue}
            </div>
          )}

          {hasHomework && (
            <div 
              key="homework-pill"
              className={`calendar-pill homework-pill ${hasHomeworkFeedback ? 'has-feedback' : ''}`}
              onClick={handleHomeworkPillClick}
            >
              Homework ({getTotalHomeworkCount()})
              <span className="homework-icon text-white ml-1">
                <FaBook className="inline-block w-3 h-3" />
              </span>
              {hasHomeworkFeedback && (
                <span className="feedback-icon text-white ml-1">
                  <FaCommentAlt className="inline-block w-3 h-3" />
                </span>
              )}
            </div>
          )}

          {hasBirthdays && (
            <div
              key="birthday-pill"
              className="calendar-pill birthday-pill"
              onClick={(e) => {
                e.stopPropagation();
                onBirthdayPillClick?.(e, date);
              }}
              title={createBirthdayTooltip()}
            >
              {isScheduleAdmin ? (
                <>
                  {scheduleBirthdays.length} {scheduleBirthdays.length === 1 ? t.birthday : t.birthdays}
                </>
              ) : (
                t.happyBirthdayCalendarPill
              )}
            </div>
          )}

          {holidayName && (
            <div
              key="holiday-pill"
              className="calendar-pill holiday-pill"
              onClick={(e) => {
                e.stopPropagation();
                onClassCountClick?.(e, date);
              }}
              title={holidayName}
            >
              {holidayName}
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 